import React from "react";
import { motion } from "framer-motion";
import { reveal, container } from "../lib/motion";
import { REVIEWS, GOOGLE_RATING, GOOGLE_REVIEW_COUNT, GOOGLE_REVIEWS_URL, GOOGLE_WRITE_REVIEW_URL } from "../data/reviews";

const GoogleG = ({ className = "h-5 w-5" }) => (
  <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

const Stars = () => (
  <span className="flex gap-0.5 text-gold" aria-label="5 out of 5 stars">
    {[...Array(5)].map((_, i) => (
      <svg key={i} className="h-4 w-4 fill-current" viewBox="0 0 20 20" aria-hidden="true">
        <path d="M10 1.5l2.6 5.3 5.9.9-4.2 4.1 1 5.8L10 14.9l-5.3 2.7 1-5.8L1.5 7.7l5.9-.9L10 1.5z" />
      </svg>
    ))}
  </span>
);

export const Reviews = () => (
  <section id="reviews" className="relative overflow-hidden bg-navy py-24 md:py-32">
    <div className="pointer-events-none absolute -right-24 top-0 h-[380px] w-[380px] rounded-full bg-teal/15 blur-[120px]" />
    <div className="relative mx-auto max-w-[1400px] px-5 md:px-10">
      <motion.div variants={container} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="mb-14 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
        <div className="max-w-2xl">
          <motion.span variants={reveal} className="mb-6 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-teal">
            <span className="h-px w-10 bg-teal" /> Google Reviews
          </motion.span>
          <motion.h2 variants={reveal} className="font-display text-4xl font-semibold leading-[0.95] tracking-tighter text-white md:text-6xl" data-testid="reviews-headline">
            What Clients Say
          </motion.h2>
        </div>
        <motion.a
          variants={reveal}
          href={GOOGLE_REVIEWS_URL}
          target="_blank"
          rel="noreferrer"
          className="group inline-flex w-fit items-center gap-4 rounded-full border border-white/15 bg-white/5 px-6 py-4 backdrop-blur transition-colors duration-300 hover:bg-white/10"
          data-testid="google-rating-badge"
        >
          <GoogleG className="h-8 w-8" />
          <span className="flex flex-col">
            <span className="flex items-center gap-2">
              <span className="font-display text-2xl font-bold leading-none text-white">{GOOGLE_RATING}</span>
              <Stars />
            </span>
            <span className="mt-1 text-xs text-white/60 transition-colors group-hover:text-white/80">Based on {GOOGLE_REVIEW_COUNT} Google reviews</span>
          </span>
        </motion.a>
      </motion.div>

      <motion.div variants={container} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} className="columns-1 gap-5 md:columns-2 lg:columns-3">
        {REVIEWS.map((r, i) => (
          <motion.figure
            key={r.name}
            variants={reveal}
            className="group mb-5 break-inside-avoid border border-white/10 bg-white/[0.04] p-8 backdrop-blur-sm transition-colors duration-300 hover:border-teal/40 hover:bg-white/[0.07]"
            data-testid={`review-card-${i}`}
          >
            <div className="flex items-center justify-between">
              <Stars />
              <GoogleG className="h-4 w-4 opacity-60 transition-opacity group-hover:opacity-100" />
            </div>
            <blockquote className="mt-5 font-sans text-[15px] leading-relaxed text-white/80">
              “{r.text}”
            </blockquote>
            <figcaption className="mt-6 flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-teal font-display text-sm font-bold text-white">
                {r.name.charAt(0)}
              </span>
              <span>
                <span className="block font-display text-sm font-semibold text-white">{r.name}</span>
                <span className="block text-xs text-white/50">Google Review</span>
              </span>
            </figcaption>
          </motion.figure>
        ))}
      </motion.div>

      <motion.div variants={container} initial="hidden" whileInView="show" viewport={{ once: true }} className="mt-12 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
        <motion.a
          variants={reveal}
          href={GOOGLE_REVIEWS_URL}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-3 rounded-full bg-teal px-7 py-3.5 font-display text-sm font-semibold text-white transition-colors duration-300 hover:bg-teal-hover"
          data-testid="read-all-reviews-btn"
        >
          Read All Reviews on Google
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M7 17L17 7M17 7H8M17 7v9" /></svg>
        </motion.a>
        <motion.a
          variants={reveal}
          href={GOOGLE_WRITE_REVIEW_URL}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-3 rounded-full border border-white/25 px-7 py-3.5 font-display text-sm font-semibold text-white transition-colors duration-300 hover:border-teal hover:text-teal"
          data-testid="leave-review-btn"
        >
          Leave Us a Review
        </motion.a>
      </motion.div>
    </div>
  </section>
);
